/** @jsxImportSource theme-ui */
import React from 'react';
import { SxProp } from 'theme-ui';

import * as dto from 'client/dto';
import theme from 'theme';

import Typography, { TypographyColors } from 'components/_common/Typography/Typography';

import { BdNoDataIcon } from '../BdNoDataIcon';

const CreateSvgMild = (
  color: keyof typeof TypographyColors,
  backgroundColor: keyof typeof TypographyColors,
  backgroundOpacity = 0.15
) => {
  return (
    <svg
      data-testid="mild"
      width="36"
      height="36"
      viewBox="0 0 36 36"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle
        opacity={backgroundOpacity.toString()}
        cx="18"
        cy="18"
        r="15"
        fill={theme.colors[backgroundColor]}
      />
      <path
        d="M18 27C22.9706 27 27 22.9706 27 18C27 13.0294 22.9706 9 18 9C13.0294 9 9 13.0294 9 18C9 22.9706 13.0294 27 18 27Z"
        stroke={theme.colors[color]}
        strokeMiterlimit="10"
      />
      <path
        d="M15 17C15.5523 17 16 16.5523 16 16C16 15.4477 15.5523 15 15 15C14.4477 15 14 15.4477 14 16C14 16.5523 14.4477 17 15 17Z"
        fill={theme.colors[color]}
      />
      <path
        d="M21 17C21.5523 17 22 16.5523 22 16C22 15.4477 21.5523 15 21 15C20.4477 15 20 15.4477 20 16C20 16.5523 20.4477 17 21 17Z"
        fill={theme.colors[color]}
      />
      <path
        d="M21 22C19.9496 22 19.1218 22 17.9914 22C16.8668 22 16.3644 22 15 22"
        stroke={theme.colors[color]}
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const CreateSvgModerate = (
  color: keyof typeof TypographyColors,
  backgroundColor: keyof typeof TypographyColors,
  backgroundOpacity = 0.15
) => {
  return (
    <svg
      data-testid="moderate"
      width="36"
      height="36"
      viewBox="0 0 36 36"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle
        opacity={backgroundOpacity.toString()}
        cx="18"
        cy="18"
        r="15"
        fill={theme.colors[backgroundColor]}
      />
      <path
        d="M18 27C22.9706 27 27 22.9706 27 18C27 13.0294 22.9706 9 18 9C13.0294 9 9 13.0294 9 18C9 22.9706 13.0294 27 18 27Z"
        stroke={theme.colors[color]}
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M15 23C15.8278 22.3706 16.8782 22 18.0086 22C19.1332 22 20.1722 22.3706 21 22.9898"
        stroke={theme.colors[color]}
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M15 17C15.5523 17 16 16.5523 16 16C16 15.4477 15.5523 15 15 15C14.4477 15 14 15.4477 14 16C14 16.5523 14.4477 17 15 17Z"
        fill={theme.colors[color]}
      />
      <path
        d="M21 17C21.5523 17 22 16.5523 22 16C22 15.4477 21.5523 15 21 15C20.4477 15 20 15.4477 20 16C20 16.5523 20.4477 17 21 17Z"
        fill={theme.colors[color]}
      />
    </svg>
  );
};

const CreateSvgSevere = (color: string, backgroundColor: string, backgroundOpacity = 0.15) => {
  return (
    <svg
      data-testid="severe"
      width="36"
      height="36"
      viewBox="0 0 36 36"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle
        opacity={backgroundOpacity.toString()}
        cx="18"
        cy="18"
        r="15"
        fill={theme.colors[backgroundColor]}
      />
      <path
        d="M18 27C22.9706 27 27 22.9706 27 18C27 13.0294 22.9706 9 18 9C13.0294 9 9 13.0294 9 18C9 22.9706 13.0294 27 18 27Z"
        stroke={theme.colors[color]}
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M15.2942 16.1586C15.9437 16.1586 16.4702 15.6321 16.4702 14.9826C16.4702 14.3332 15.9437 13.8066 15.2942 13.8066C14.6447 13.8066 14.1182 14.3332 14.1182 14.9826C14.1182 15.6321 14.6447 16.1586 15.2942 16.1586Z"
        fill={theme.colors[color]}
      />
      <path
        d="M20.7063 16.1586C21.3558 16.1586 21.8823 15.6321 21.8823 14.9826C21.8823 14.3332 21.3558 13.8066 20.7063 13.8066C20.0568 13.8066 19.5303 14.3332 19.5303 14.9826C19.5303 15.6321 20.0568 16.1586 20.7063 16.1586Z"
        fill={theme.colors[color]}
      />
      <path
        d="M14.7841 22.1944C14.5861 22.1944 14.4361 22.0264 14.4541 21.8284C14.6221 19.8124 16.1761 18.2344 18.0721 18.2344C19.9681 18.2344 21.5221 19.8124 21.6901 21.8284C21.7081 22.0204 21.5521 22.1944 21.3601 22.1944H14.7841V22.1944Z"
        stroke={theme.colors[color]}
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export type BdSeverityIconLiteral = 'no-data' | 'mild' | 'moderate' | 'severe';

const getIcon = (type: dto.SeverityDisruptionFactorType, dark: boolean) => {
  switch (type) {
    case dto.SeverityDisruptionFactorType.Mild:
      return CreateSvgMild(dark ? 'stone20' : 'stone70', 'stone50', 0.15);
    case dto.SeverityDisruptionFactorType.MildToModerate:
      return CreateSvgModerate(dark ? 'saffron20' : 'saffron70', 'saffron70', 0.15);
    case dto.SeverityDisruptionFactorType.Moderate:
      return CreateSvgModerate(dark ? 'saffron20' : 'saffron70', 'saffron70', 0.15);
    case dto.SeverityDisruptionFactorType.ModerateToSevere:
      return CreateSvgSevere(dark ? 'starfish40' : 'starfish100', 'starfish100', 0.15);
    case dto.SeverityDisruptionFactorType.Severe:
      return CreateSvgSevere(dark ? 'starfish40' : 'starfish100', 'starfish100', 0.15);
    case dto.SeverityDisruptionFactorType.NoData:
    default:
      return <BdNoDataIcon dark={dark} />;
  }
};

const getDescription = (type: dto.SeverityDisruptionFactorType) => {
  switch (type) {
    case dto.SeverityDisruptionFactorType.Mild:
      return 'Mild';
    case dto.SeverityDisruptionFactorType.MildToModerate:
      return 'Mild to Moderate';
    case dto.SeverityDisruptionFactorType.Moderate:
      return 'Moderate';
    case dto.SeverityDisruptionFactorType.ModerateToSevere:
      return 'Moderate to Severe';
    case dto.SeverityDisruptionFactorType.Severe:
      return 'Severe';
    case dto.SeverityDisruptionFactorType.NoData:
    default:
      return 'No data available';
  }
};

type BdSeverityIconProps = SxProp & {
  type: dto.SeverityDisruptionFactorType;
  /** Changes colour for dark backgrounds */
  dark?: boolean;
  labelPosition?: 'top' | 'bottom';
  /** Set to true for Icon only */
  simple?: boolean;
};

const BdSeverityIcon: React.FC<BdSeverityIconProps> = ({
  type,
  dark = false,
  labelPosition = 'bottom',
  simple = false,
  ...props
}) => {
  return (
    <div data-testid="severity-icon" sx={{ display: 'flex' }} {...props}>
      {getIcon(type, dark)}
      {!simple && (
        <div
          data-testid="description"
          sx={{
            display: 'flex',
            flexDirection: labelPosition === 'bottom' ? 'column' : 'column-reverse',
            ml: '7px',
          }}
        >
          <Typography
            variant="subtitle2"
            color={dark ? 'stone10' : 'stone90'}
            marginBottom={labelPosition === 'bottom' ? '4px' : '0'}
          >
            {getDescription(type)}
          </Typography>
          <Typography
            variant="caption"
            color={dark ? 'deepSea40' : 'stone90'}
            marginBottom={labelPosition === 'bottom' ? '0' : '4px'}
          >
            Pathogen severity
          </Typography>
        </div>
      )}
    </div>
  );
};

export default BdSeverityIcon;
